import Collapse from '@rio-cloud/rio-uikit/Collapse';
import { type MouseEvent, useState } from 'react';
import { NavLink } from 'react-router';

import { routes } from '../../routes/routes';
import { adminMenuItems } from './adminMenuItems';
import { buildAdminMenuSubmenuId, useAdminMenuLinkHandler } from './adminMenuUtils';

type MenuItemDescriptor = {
    icon: string;
    subline: string;
};

type SubItemDescriptor = {
    icon: string;
};

const menuItemDescriptors: Record<string, MenuItemDescriptor> = {
    ACCOUNTS: { icon: 'icon-user', subline: 'Manage customer accounts' },
    'PARTNER INTEGRATIONS': {
        icon: 'icon-handshake',
        subline: 'Create and manage partner integration clients',
    },
    'CLIENT METRICS': { icon: 'icon-stats', subline: 'Review client metrics' },
    ASSETS: { icon: 'icon-truck', subline: 'View fleet assets' },
    'DEVICE STATE': { icon: 'icon-engine', subline: 'Monitor device health' },
    'INBOUND CONNECTIONS': { icon: 'icon-arrows-horizontal', subline: 'Review inbound connections' },
    ACTIVATION: { icon: 'icon-shopping-cart', subline: 'Manage Marketplace actions' },
    BILLING: { icon: 'icon-euro-note', subline: 'Manage discounts, usage and payment methods' },
    'IOT EVENTS': { icon: 'icon-broadcast', subline: 'Inspect IoT event logs' },
    RENTAL: { icon: 'icon-van', subline: 'Manage RIO4Rental' },
    'TRANSPORT ORDERS': { icon: 'icon-clipboard', subline: 'Inspect transport order lifecycles' },
};

const getMenuItemDescriptor = (label: string): MenuItemDescriptor =>
    menuItemDescriptors[label] ?? { icon: 'icon-menu-hamburger', subline: 'Open section' };

const subItemDescriptors: Record<string, SubItemDescriptor> = {
    Activation: { icon: 'icon-support' },
    'Free Trial': { icon: 'icon-support' },
    'Integrated Offer - Packages': { icon: 'icon-hand-right' },
    'Integrated Offer - Booking Intents': { icon: 'icon-hand-right' },
    Utilities: { icon: 'icon-magic-wand' },
};

const getSubItemDescriptor = (label: string): SubItemDescriptor =>
    subItemDescriptors[label] ?? { icon: 'icon-menu-hamburger' };

const renderMenuItemContent = (label: string, descriptor: MenuItemDescriptor) => (
    <span className='display-flex align-items-start gap-10'>
        <span
            className={`uikit-icon ${descriptor.icon} text-size-18 text-color-primary margin-top-2`}
            aria-hidden='true'
        />
        <span className='display-flex flex-column line-height-125rel'>
            <span className='text-size-14 text-medium text-color-darkest'>{label}</span>
            <span className='text-size-12 text-color-gray'>{descriptor.subline}</span>
        </span>
    </span>
);

const initialSubmenuState = adminMenuItems.reduce<Record<string, boolean>>((state, item) => {
    if (item.subItems?.length) {
        state[item.label] = false;
    }
    return state;
}, {});

const AdminMenuList = () => {
    const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>(initialSubmenuState);
    const handleAdminMenuLink = useAdminMenuLinkHandler();

    const handleToggleSubmenu = (label: string) => {
        setOpenSubmenus(currentState => ({
            ...currentState,
            [label]: !currentState[label],
        }));
    };

    const handleSubItemClick =
        (href: string) =>
        (event: MouseEvent<HTMLAnchorElement>): void => {
            event.stopPropagation();
            handleAdminMenuLink(href)(event);
        };

    const menuItems = adminMenuItems.map(item => {
        const hasSubmenu = Boolean(item.subItems?.length);
        const descriptor = getMenuItemDescriptor(item.label);
        const submenuId = hasSubmenu ? buildAdminMenuSubmenuId(item.label) : undefined;
        const isSubmenuOpen = openSubmenus[item.label] ?? false;

        if (hasSubmenu) {
            return {
                key: item.label,
                route: (
                    <div className='rounded' onClick={event => event.stopPropagation()}>
                        <NavLink
                            to={routes.DEFAULT}
                            className='display-flex align-items-start justify-content-between rounded text-decoration-none hover-bg-lightest padding-10'
                            aria-expanded={isSubmenuOpen ? 'true' : 'false'}
                            aria-controls={submenuId}
                            onClick={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleToggleSubmenu(item.label);
                            }}
                        >
                            <span className='flex-1-1'>{renderMenuItemContent(item.label, descriptor)}</span>
                            <span
                                className={`uikit-icon ${
                                    isSubmenuOpen ? 'icon-chevron-up' : 'icon-chevron-down'
                                } text-size-12 text-color-gray margin-top-2`}
                                aria-hidden='true'
                            />
                        </NavLink>
                        <Collapse open={isSubmenuOpen} unmountOnExit>
                            <div id={submenuId} className='display-flex flex-column gap-5 margin-top-5 padding-left-20'>
                                {item.subItems?.map(subItem => (
                                    <NavLink
                                        key={subItem.label}
                                        to={subItem.href}
                                        className='display-flex align-items-center gap-5 text-size-12 text-decoration-none text-color-darkest hover-text-decoration-underline'
                                        onClick={handleSubItemClick(subItem.href)}
                                    >
                                        <span
                                            className={`uikit-icon ${getSubItemDescriptor(subItem.label).icon} text-size-12 text-color-primary`}
                                            aria-hidden='true'
                                        />
                                        <span>{subItem.label}</span>
                                    </NavLink>
                                ))}
                            </div>
                        </Collapse>
                    </div>
                ),
            };
        }

        return {
            key: item.label,
            route: (
                <NavLink
                    to={item.href ?? routes.DEFAULT}
                    className='display-block rounded text-decoration-none hover-bg-lightest padding-5'
                    onClick={item.href ? handleAdminMenuLink(item.href) : undefined}
                >
                    {renderMenuItemContent(item.label, descriptor)}
                </NavLink>
            ),
        };
    });

    return (
        <nav aria-label='Admin menu' className='display-flex flex-column padding-10 gap-5 max-width-350'>
            {menuItems.map(menuItem => (
                <div key={menuItem.key}>{menuItem.route}</div>
            ))}
        </nav>
    );
};

export default AdminMenuList;
